import { createElement } from 'react';
import {
  Utensils,
  Coffee,
  Martini,
  CakeSlice,
  Umbrella,
  Trees,
  Drama,
  Building2,
  Bus,
  Footprints,
  Bike,
  Waves,
  Sailboat,
  Dumbbell,
  Volleyball,
  Baby,
  Snowflake,
  Car,
  Ship,
  Plane,
  TrainFront,
  Pill,
  Hospital,
  Shield,
  PawPrint,
  ShoppingCart,
  Banknote,
  Fuel,
  Store,
  PartyPopper,
  Tent,
  Music,
  MapPin,
  type LucideIcon,
} from 'lucide-react';
import type { RecommendationCategory } from '@/types/database';

/**
 * Lucide icon mapping for recommendation categories.
 *
 * This is intentionally SEPARATE from the emoji `icon` field returned by
 * getCategoryInfo() in useLocalRecommendations.ts. The emoji values are still
 * used by the landlord-side editor and GuestExploreTab, so they must NOT change.
 * The guest Host card uses this lucide map instead for visual consistency with
 * the rest of the portal.
 */
export const RECOMMENDATION_LUCIDE_ICONS: Record<RecommendationCategory, LucideIcon> = {
  // Food & Drink
  restaurant: Utensils,
  cafe: Coffee,
  bar: Martini,
  pastry: CakeSlice,

  // Nature & Sights
  beach: Umbrella,
  national_park: Trees,
  attraction: Drama,
  city: Building2,

  // Activities & Sports
  excursion: Bus,
  hiking: Footprints,
  cycling: Bike,
  diving: Waves,
  water_sports: Waves,
  gym: Dumbbell,
  sports_court: Volleyball,
  playground: Baby,
  rental_sea: Sailboat,
  rental_snow: Snowflake,

  // Transport
  bus: Bus,
  taxi: Car,
  ferry: Ship,
  airport: Plane,
  train: TrainFront,
  rental_car: Car,

  // Services & Emergency
  pharmacy: Pill,
  hospital: Hospital,
  police: Shield,
  vet: PawPrint,
  shop: ShoppingCart,
  atm: Banknote,
  gas_station: Fuel,
  market: Store,

  // Events
  event: PartyPopper,
  festival: Tent,
  concert: Music,

  // Other
  other: MapPin,
};

/**
 * Get the lucide icon component for a recommendation category.
 * Falls back to MapPin for unknown / unmapped categories.
 */
export function getRecommendationIcon(category: RecommendationCategory): LucideIcon {
  return RECOMMENDATION_LUCIDE_ICONS[category] ?? MapPin;
}

/**
 * The category's icon as an element. Rendering a component looked up during
 * render (`const Icon = getRecommendationIcon(x); <Icon/>`) remounts it on every
 * render — createElement keeps it a plain element.
 */
export function RecommendationIcon({ category, className }: { category: RecommendationCategory; className?: string }) {
  return createElement(getRecommendationIcon(category), { className });
}
